import { Fragment, useCallback, useEffect, useState } from "react";
import { FileDown } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { eachDayOfInterval, format } from "date-fns";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RINKS, RINK_LABEL, RINK_BADGE, euro } from "@/lib/rinks";

const SEASON_DAYS = eachDayOfInterval({
  start: new Date("2026-11-14T00:00:00"),
  end: new Date("2027-01-17T00:00:00"),
}).map((d) => format(d, "yyyy-MM-dd"));

const SEASON_SET = new Set(SEASON_DAYS);

const MONEY = [
  ["redeemed_online", "Redeemed Online"],
  ["box_office_sales", "Box Office Sales"],
  ["credit_card_sales", "Credit Card Sales"],
  ["group_sales", "Group Sales"],
];
const NUMS = [
  ["box_office_num", "Box Office Att."],
  ["group_num", "Group Att."],
];

const REQUIRED_PAIRS = [
  ["box_office_sales", "box_office_num", "Box Office Att."],
  ["group_sales", "group_num", "Group Att."],
];

const COLS = [
  { key: "redeemed_online", label: "Redeemed Online €", type: "money" },
  { key: "online_att", label: "Online Att.", type: "computed" },
  { key: "box_office_sales", label: "Box Office Sales €", type: "money" },
  { key: "box_office_num", label: "Box Office Att.", type: "num" },
  { key: "credit_card_sales", label: "Credit Card Sales €", type: "money" },
  { key: "credit_card_num", label: "Credit Card Att.", type: "computed" },
  { key: "group_sales", label: "Group Sales €", type: "money" },
  { key: "group_num", label: "Group Att.", type: "num" },
  { key: "school_sales", label: "School Sales €", type: "computed_money" },
  { key: "school_num", label: "School Att.", type: "computed" },
];

export default function SummaryPage() {
  const { user } = useAuth();
  const canEdit = user.role !== "viewer";
  const visibleRinks = user.role === "staff" ? [user.rink] : RINKS;
  const [searchParams] = useSearchParams();
  const rinkParam = searchParams.get("rink");
  const [rink, setRink] = useState(visibleRinks.includes(rinkParam) ? rinkParam : visibleRinks[0]);
  useEffect(() => {
    if (visibleRinks.includes(rinkParam)) setRink(rinkParam);
  }, [rinkParam]);
  const [rows, setRows] = useState({});
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);
  const [dates, setDates] = useState(SEASON_DAYS);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/figures", { params: { rink } });
      const map = {};
      data.forEach((d) => { map[d.date] = d; });
      setRows(map);
      setDates([...SEASON_DAYS, ...data.map((d) => d.date).filter((dt) => !SEASON_SET.has(dt))].sort());
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [rink]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const setTop = () => {
      const h = document.querySelector("header");
      if (h) document.documentElement.style.setProperty("--summary-top", `${h.offsetHeight}px`);
    };
    setTop();
    window.addEventListener("resize", setTop);
    return () => window.removeEventListener("resize", setTop);
  }, []);

  const setCell = (date, field, value) =>
    setRows((r) => ({ ...r, [date]: { ...(r[date] || {}), [field]: value } }));

  const saveRow = async (date, extra = {}) => {
    const row = { ...(rows[date] || {}), ...extra };
    if (!row.closed) {
      for (const [sales, num, label] of REQUIRED_PAIRS) {
        if (Number(row[sales]) > 0 && !(Number(row[num]) > 0)) {
          setSaved((s) => ({ ...s, [date]: "" }));
          toast.error(`${label} is required when ${sales.replace(/_/g, " ")} has a value.`);
          return;
        }
      }
    }
    const payload = {};
    [...MONEY, ...NUMS].forEach(([f]) => { payload[f] = Number(row[f]) || 0; });
    payload.credit_card_num = Math.floor((Number(row.credit_card_sales) || 0) / 16);
    const gsEmpty = row.group_sales === "" || row.group_sales == null;
    const gnEmpty = row.group_num === "" || row.group_num == null;
    payload.group_manual = !!row.group_manual && !(gsEmpty && gnEmpty);
    payload.closed = !!row.closed;
    setSaved((s) => ({ ...s, [date]: "Saving…" }));
    try {
      await api.put(`/figures/${rink}/${date}`, payload);
      if (!payload.group_manual && row.group_manual) {
        setRows((r) => ({ ...r, [date]: { ...(r[date] || {}), group_manual: false, group_sales: row.auto_group_sales ?? 0, group_num: row.auto_group_att ?? 0 } }));
      }
      setSaved((s) => ({ ...s, [date]: "Saved" }));
      setTimeout(() => setSaved((s) => ({ ...s, [date]: "" })), 1500);
    } catch (e) {
      setSaved((s) => ({ ...s, [date]: "" }));
      toast.error(errMsg(e));
    }
  };

  const downloadWeeklyPdf = async () => {
    try {
      const { data } = await api.get(`/figures/${rink}/weekly.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${rink}-weekly-attendance.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const totals = { online_att: 0, credit_card_num: 0, school_sales: 0, school_num: 0 };
  dates.forEach((d) => {
    const row = rows[d] || {};
    [...MONEY, ...NUMS].forEach(([f]) => { totals[f] = (totals[f] || 0) + (Number(row[f]) || 0); });
    totals.online_att += Math.floor((Number(row.redeemed_online) || 0) / 16);
    totals.credit_card_num += Math.floor((Number(row.credit_card_sales) || 0) / 16);
    totals.school_sales += Number(row.school_sales) || 0;
    totals.school_num += Number(row.school_num) || 0;
  });
  totals.day_money = MONEY.reduce((a, [f]) => a + (totals[f] || 0), 0) + totals.school_sales;
  totals.day_att = totals.online_att + (totals.box_office_num || 0) + (totals.credit_card_num || 0) + (totals.group_num || 0) + totals.school_num;

  let weekBucket = null;

  return (
    <div className="max-w-[1600px] mx-auto p-4 md:p-6" data-testid="summary-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">Daily figures</div>
          <h1 className="font-display text-3xl md:text-4xl uppercase text-slate-900">
            {user.role === "staff" ? RINK_LABEL[user.rink] : "Summary"}
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Season 14 Nov 2026 – 17 Jan 2027. Online Att. = Redeemed Online ÷ 16 and Credit Card Att. = Credit Card Sales ÷ 16 (both rounded down).
            School Sales € and School Att. auto-fill from that day&apos;s school bookings (kids × price per child).
            {!canEdit && " Read-only — ask an admin or staff member to enter figures."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={downloadWeeklyPdf}
          data-testid="summary-weekly-pdf"
          className="inline-flex items-center gap-1.5 bg-slate-900 text-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider shadow-[2px_2px_0px_0px_#0f172a] hover:bg-slate-700 transition-colors"
        >
          <FileDown className="w-4 h-4" /> Weekly PDF
        </button>
        {visibleRinks.length > 1 && (
          <div className="flex gap-2 bg-slate-100 p-1 border-2 border-slate-900 shadow-[2px_2px_0px_0px_#0f172a]" data-testid="summary-rink-switcher">
            {visibleRinks.map((r) => (
              <button
                key={r}
                onClick={() => setRink(r)}
                data-testid={`summary-tab-${r}`}
                className={rink === r
                  ? `${RINK_BADGE[r]} border-2 border-slate-900 font-extrabold uppercase text-xs px-4 py-2 tracking-wider shadow-[2px_2px_0px_0px_#0f172a]`
                  : "bg-white text-slate-900 border-2 border-slate-900 hover:bg-slate-200 font-bold uppercase text-xs px-4 py-2 tracking-wider transition-colors"}
              >
                {RINK_LABEL[r]}
              </button>
            ))}
          </div>
        )}
        </div>
      </div>

      <div
        className="card-brutal shadow-[6px_6px_0px_0px_#0f172a]"
        data-testid="summary-table"
      >
        <table className="w-full table-fixed border-collapse [&_th]:border-l-2 [&_th]:border-slate-900 [&_td]:border-l-2 [&_td]:border-slate-900 [&_th:first-child]:border-l-0 [&_td:first-child]:border-l-0">
          <thead>
            {!loading && (
              <tr className="bg-amber-300" data-testid="summary-totals">
                <td className="p-2 w-[110px] sticky z-20 bg-amber-300 align-bottom text-center" style={{ top: "var(--summary-top, 66px)" }}>
                  <div className="font-display text-sm uppercase font-bold">Season Totals</div>
                </td>
                <td className="p-2 w-[64px] sticky z-20 bg-amber-300" style={{ top: "var(--summary-top, 66px)" }}></td>
                {COLS.map((col) => (
                  <td key={col.key} className={`p-2 align-bottom text-center sticky z-20 ${col.type !== "money" && col.type !== "computed_money" ? "w-[92px] bg-amber-200" : "bg-amber-300"}`} style={{ top: "var(--summary-top, 66px)" }} data-testid={`summary-total-${col.key}`}>
                    <div className="font-mono-tight uppercase text-[9px] tracking-widest text-slate-600">{col.label}</div>
                    <div className="font-mono-tight text-sm font-bold">{col.type === "money" || col.type === "computed_money" ? euro(totals[col.key] || 0) : totals[col.key] || 0}</div>
                  </td>
                ))}
                <td className="p-2 w-[110px] align-bottom sticky z-20 bg-amber-200 text-center" style={{ top: "var(--summary-top, 66px)" }} data-testid="summary-total-day-money">
                  <div className="font-mono-tight uppercase text-[9px] tracking-widest text-slate-600">Total Sales</div>
                  <div className="font-mono-tight text-sm font-bold">{euro(totals.day_money)}</div>
                </td>
                <td className="p-2 w-[92px] align-bottom sticky z-20 bg-amber-200 text-center" style={{ top: "var(--summary-top, 66px)" }} data-testid="summary-total-day-attendance">
                  <div className="font-mono-tight uppercase text-[9px] tracking-widest text-slate-600">Total Att.</div>
                  <div className="font-mono-tight text-sm font-bold">{totals.day_att}</div>
                </td>
              </tr>
            )}
            <tr className="bg-slate-900 text-white font-mono-tight uppercase text-[10px] tracking-widest border-t-2 border-slate-900">
              <th className="p-2.5 text-center font-bold w-[110px]">Date</th>
              <th className="p-2.5 text-center font-bold w-[64px]">Closed</th>
              {COLS.map((col) => (
                <th key={col.key} className={`p-2.5 text-center font-bold ${col.type !== "money" && col.type !== "computed_money" ? "bg-sky-800 w-[92px]" : ""}`}>
                  {col.label}
                </th>
              ))}
              <th className="p-2.5 text-center font-bold w-[110px] bg-sky-800">Day Total €</th>
              <th className="p-2.5 text-center font-bold w-[92px] bg-sky-800">Day Att.</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={14} className="p-8 text-center font-mono-tight text-xs uppercase tracking-widest text-slate-500" data-testid="summary-loading">Loading…</td></tr>
            )}
            {!loading && dates.map((date) => {
              const row = rows[date] || {};
              const onlineAtt = Math.floor((Number(row.redeemed_online) || 0) / 16);
              const ccAtt = Math.floor((Number(row.credit_card_sales) || 0) / 16);
              const dayMoney = MONEY.reduce((a, [f]) => a + (Number(row[f]) || 0), 0) + (Number(row.school_sales) || 0);
              const dayAtt = onlineAtt + (Number(row.box_office_num) || 0) + ccAtt + (Number(row.group_num) || 0) + (Number(row.school_num) || 0);
              if (!weekBucket) weekBucket = { vals: {}, money: 0, att: 0 };
              [...MONEY, ...NUMS, ["school_sales"], ["school_num"]].forEach(([f]) => { weekBucket.vals[f] = (weekBucket.vals[f] || 0) + (Number(row[f]) || 0); });
              weekBucket.vals.online_att = (weekBucket.vals.online_att || 0) + onlineAtt;
              weekBucket.vals.credit_card_num = (weekBucket.vals.credit_card_num || 0) + ccAtt;
              weekBucket.money += dayMoney;
              weekBucket.att += dayAtt;
              const dow = new Date(`${date}T00:00:00`).getDay();
              const weekEnd = dow === 0 || date === dates[dates.length - 1];
              const wkMonday = new Date(`${date}T00:00:00`);
              wkMonday.setDate(wkMonday.getDate() - ((dow + 6) % 7));
              const wk = weekBucket;
              if (weekEnd) weekBucket = null;
              return (
                <Fragment key={date}>
                <tr className={`border-t-2 border-slate-900 transition-colors ${row.closed ? "bg-slate-200 text-slate-500" : "hover:bg-sky-50/60"}`} data-testid={`summary-row-${date}`}>
                  <td className="p-2.5 text-sm font-bold whitespace-nowrap text-center">{format(new Date(`${date}T00:00:00`), "EEE dd MMM yy")}</td>
                  <td className="p-1.5 text-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-slate-700"
                      disabled={user.role !== "admin"}
                      checked={!!row.closed}
                      onChange={(e) => { setCell(date, "closed", e.target.checked); saveRow(date, { closed: e.target.checked }); }}
                      title={user.role === "admin" ? "Day closed in full" : "Only admin can close or reopen a day"}
                      data-testid={`summary-closed-${date}`}
                    />
                  </td>
                  {COLS.map((col) => {
                    if (col.type === "computed" || col.type === "computed_money") {
                      const v = col.key === "credit_card_num" ? ccAtt
                        : col.key === "online_att" ? onlineAtt
                        : Number(row[col.key]) || 0;
                      return (
                        <td key={col.key} className={`p-2.5 font-mono-tight text-sm font-bold text-center ${row.closed ? "bg-slate-300" : "bg-sky-100"}`} data-testid={`summary-computed-${col.key}-${date}`}>
                          {col.type === "computed_money" ? euro(v) : v}
                        </td>
                      );
                    }
                    return (
                      <td key={col.key} className={`p-1.5 ${col.type === "num" ? (row.closed ? "bg-slate-300" : "bg-sky-100") : ""}`}>
                        <input
                          type="number" min="0" step={col.type === "money" ? "0.01" : "1"}
                          className={`input-brutal !p-1.5 !shadow-none !text-xs !w-full !min-w-0 !text-center mx-auto block ${row.closed ? "!bg-slate-100 !text-slate-500" : ""}`}
                          disabled={!canEdit || !!row.closed}
                          value={row[col.key] ?? ""}
                          placeholder="0"
                          onChange={(e) => {
                            setCell(date, col.key, e.target.value);
                            if (col.key === "group_sales" || col.key === "group_num") setCell(date, "group_manual", true);
                          }}
                          onBlur={() => canEdit && saveRow(date)}
                          data-testid={`summary-input-${col.key}-${date}`}
                        />
                      </td>
                    );
                  })}
                  <td className={`p-2.5 font-mono-tight text-sm font-bold text-center ${row.closed ? "bg-slate-300" : "bg-sky-200"}`} data-testid={`summary-day-money-${date}`}>
                    {euro(dayMoney)}
                  </td>
                  <td className={`p-2.5 font-mono-tight text-sm font-bold text-center ${row.closed ? "bg-slate-300" : "bg-sky-200"}`} data-testid={`summary-day-attendance-${date}`}>
                    {dayAtt}
                    <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-normal" data-testid={`summary-saved-${date}`}>{saved[date] || " "}</div>
                  </td>
                </tr>
                {weekEnd && wk && (
                  <tr className="border-t-2 border-b-4 border-slate-900 bg-amber-300" data-testid={`summary-week-total-${date}`}>
                    <td colSpan={2} className="p-2 text-center">
                      <div className="font-mono-tight uppercase text-[9px] tracking-widest text-slate-600">Week total</div>
                      <div className="font-mono-tight text-[11px] font-bold whitespace-nowrap">{format(wkMonday, "dd MMM")} – {format(new Date(`${date}T00:00:00`), "dd MMM yy")}</div>
                    </td>
                    {COLS.map((col) => (
                      <td key={col.key} className={`p-2 text-center font-mono-tight text-sm font-bold ${col.type !== "money" && col.type !== "computed_money" ? "bg-amber-200" : ""}`} data-testid={`summary-week-${col.key}-${date}`}>
                        {col.type === "money" || col.type === "computed_money" ? euro(wk.vals[col.key] || 0) : wk.vals[col.key] || 0}
                      </td>
                    ))}
                    <td className="p-2 text-center font-mono-tight text-sm font-bold bg-amber-200" data-testid={`summary-week-money-${date}`}>{euro(wk.money)}</td>
                    <td className="p-2 text-center font-mono-tight text-sm font-bold bg-amber-200" data-testid={`summary-week-att-${date}`}>{wk.att}</td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
